import React, { type ReactNode } from "react";
import { SportsIconsSVG } from "../Games/utils.js";
import { CricketTeamLogo } from "../shared/logos";
import { favSportIcon } from "./profileFavSportIcons";
import { footballLogoUsesWhiteTile, footballTeamLogoUrl } from "./profileFavFootballTeams";
import { iplLogoUsesWhiteTile, iplTeamLogoUrl } from "./profileFavIplTeams";
import { cricketIntLogoUsesWhiteTile, cricketIntTeamLogoUrl } from "./profileFavCricketIntTeams";
import { cricketerLogoUrl } from "./profileFavCricketers";

const SIZE_CLASSES = {
  default: {
    icon: "h-[18px] w-[18px]",
    img: "h-[22px] w-[22px]",
    cricketLogo: "28px",
    tourFallback: "text-[8px]",
  },
  large: {
    icon: "h-7 w-7",
    img: "h-8 w-8",
    cricketLogo: "36px",
    tourFallback: "text-[11px]",
  },
  /** 30×30 profile strip — full bleed (no tile border). */
  stripFlat: {
    icon: "h-[30px] w-[30px]",
    img: "h-[30px] w-[30px] min-h-[30px] min-w-[30px]",
    cricketLogo: "30px",
    tourFallback: "text-[9px]",
  },
  /** 30×30 outer tile with 1px border — graphics fill inner ~28×28. */
  stripTinted: {
    icon: "h-[28px] w-[28px]",
    img: "h-[28px] w-[28px] min-h-[28px] min-w-[28px]",
    cricketLogo: "28px",
    tourFallback: "text-[8px]",
  },
} as const;

/** Solid white — same line/read as sport tabs inactive state on dark UI. */
const LINE = "#FFFFFF";

export type FavouritesSportIconSize = keyof typeof SIZE_CLASSES;

/**
 * Maps profile favourite chips to sport/team assets. Favourite sports use raster icons from
 * {@link favSportIcon}. Favourite cricket team (t1) uses CricketTeamLogo — same CDN resolution as match cards.
 */
export function FavouritesSportIcon({
  chipKey,
  label,
  size = "default",
}: {
  chipKey: string;
  label: string;
  size?: FavouritesSportIconSize;
}): ReactNode {
  const s = SIZE_CLASSES[size];
  if (chipKey === "sport" || chipKey.startsWith("sport-")) {
    return favSportIcon(label.trim(), LINE, `${s.icon} flex-shrink-0`);
  }
  if (chipKey === "t1") {
    const name = label.trim();
    if (name) {
      return (
        <CricketTeamLogo
          teamName={name}
          width={s.cricketLogo}
          height={s.cricketLogo}
          className="shrink-0 justify-center [&_img]:object-contain"
        />
      );
    }
    return SportsIconsSVG.cricket(LINE, s.icon);
  }
  if (chipKey === "t2") return SportsIconsSVG.football(LINE, s.icon);
  if (chipKey.startsWith("fb-")) {
    const slug = chipKey.slice(3);
    if (slug) {
      const whiteTile = footballLogoUsesWhiteTile(slug);
      const img = (
        <img
          src={footballTeamLogoUrl(slug)}
          alt=""
          className={
            whiteTile ? "max-h-full max-w-full object-contain" : `${s.img} shrink-0 object-contain`
          }
          loading="lazy"
        />
      );
      if (whiteTile) {
        return (
          <span
            className={`inline-flex shrink-0 items-center justify-center rounded-none bg-white p-0.5 box-border ${s.img}`}
          >
            {img}
          </span>
        );
      }
      return img;
    }
  }
  if (chipKey.startsWith("ipl-")) {
    const slug = chipKey.slice(4);
    if (slug) {
      const whiteTile = iplLogoUsesWhiteTile(slug);
      const img = (
        <img
          src={iplTeamLogoUrl(slug)}
          alt=""
          className={
            whiteTile ? "max-h-full max-w-full object-contain" : `${s.img} shrink-0 object-contain`
          }
          loading="lazy"
        />
      );
      if (whiteTile) {
        return (
          <span
            className={`inline-flex shrink-0 items-center justify-center rounded-none bg-white p-0.5 box-border ${s.img}`}
          >
            {img}
          </span>
        );
      }
      return img;
    }
  }
  if (chipKey.startsWith("cint-")) {
    const slug = chipKey.slice(5);
    if (slug) {
      const whiteTile = cricketIntLogoUsesWhiteTile(slug);
      const img = (
        <img
          src={cricketIntTeamLogoUrl(slug)}
          alt=""
          className={
            whiteTile ? "max-h-full max-w-full object-contain" : `${s.img} shrink-0 object-contain`
          }
          loading="lazy"
        />
      );
      if (whiteTile) {
        return (
          <span
            className={`inline-flex shrink-0 items-center justify-center rounded-none bg-white p-0.5 box-border ${s.img}`}
          >
            {img}
          </span>
        );
      }
      return img;
    }
  }
  if (chipKey.startsWith("team-")) {
    const name = label.trim();
    if (name) {
      return (
        <CricketTeamLogo
          teamName={name}
          width={s.cricketLogo}
          height={s.cricketLogo}
          className="shrink-0 justify-center [&_img]:object-contain"
        />
      );
    }
    return SportsIconsSVG.cricket(LINE, s.icon);
  }
  if (chipKey.startsWith("cr-")) {
    const slug = chipKey.slice(3);
    if (slug) {
      return (
        <img
          src={cricketerLogoUrl(slug)}
          alt=""
          className={`${s.img} shrink-0 object-cover`}
          loading="lazy"
        />
      );
    }
  }
  if (chipKey === "tour") return SportsIconsSVG.cricket(LINE, s.icon);
  return (
    <span
      className={`font-anton ${s.tourFallback} font-normal uppercase leading-none tracking-normal text-white/40 [leading-trim:none]`}
    >
      {label.slice(0, 3).toUpperCase()}
    </span>
  );
}
